import React, {
    useEffect,
    useRef,
    useState
} from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Alert
} from "@mui/material";

import {
    getCreditJVCurrentMonth,
    getCreditJV,
    saveCreditJV
} from "../../../services/creditJVService";
import PageHeader from "../../../utils/PageHeader";


// ============================================================
// MONTH NAMES
// ============================================================

const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
];


// ============================================================
// COMPONENT
// ============================================================

const CreditJVDataEntry = () => {


    // ========================================================
    // STATE
    // ========================================================

    const [month, setMonth] =
        useState("");

    const [year, setYear] =
        useState("");

    const [iohPohJV, setIohPohJV] =
        useState("0");

    const [electricalJV, setElectricalJV] =
        useState("0");

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [message, setMessage] =
        useState("");

    const [messageType, setMessageType] =
        useState("");


    // ========================================================
    // REFS
    // ========================================================

    const iohPohRef =
        useRef(null);

    const electricalRef =
        useRef(null);

    const saveRef =
        useRef(null);


    // ========================================================
    // MONTH NAME
    // ========================================================

    const monthName =
        month
            ? monthNames[Number(month)]
            : "";


    // ========================================================
    // LOAD CURRENT MONTH
    // ========================================================

    const loadCurrentMonth = async () => {

        try {

            setLoading(true);

            setMessage("");
            setMessageType("");


            const res =
                await getCreditJVCurrentMonth();


            const data =
                res.data.data;


            const currentMonth =
                Number(data.month);

            const currentYear =
                Number(data.year);


            setMonth(currentMonth);

            setYear(currentYear);


            // IMPORTANT:
            // Use returned values directly.

            await loadCreditJV(
                currentMonth,
                currentYear
            );


        } catch (err) {

            console.error(
                "Current month error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to load current month"
            );

            setMessageType("error");


        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // LOAD CREDIT JV DATA
    // ========================================================

    const loadCreditJV = async (
        selectedMonth,
        selectedYear
    ) => {

        try {

            const res =
                await getCreditJV(
                    selectedMonth,
                    selectedYear
                );


            const data =
                res.data.data;


            if (data) {

                setIohPohJV(
                    data.IOHPOHJV !== null &&
                    data.IOHPOHJV !== undefined
                        ? String(data.IOHPOHJV)
                        : "0"
                );


                setElectricalJV(
                    data.ELECTRICALJV !== null &&
                    data.ELECTRICALJV !== undefined
                        ? String(data.ELECTRICALJV)
                        : "0"
                );

            } else {

                // No entry for this month

                setIohPohJV("0");

                setElectricalJV("0");

            }


        } catch (err) {

            console.error(
                "Credit JV load error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to load Credit JV data"
            );

            setMessageType("error");

        }

    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadCurrentMonth();

    }, []);


    // ========================================================
    // SAVE CREDIT JV
    // ========================================================

    const handleSave = async () => {

        setMessage("");
        setMessageType("");


        // ----------------------------------------------------
        // GET TEXT VALUES
        // ----------------------------------------------------

        const iohPohText =
            String(iohPohJV).trim();

        const electricalText =
            String(electricalJV).trim();


        // ----------------------------------------------------
        // VALIDATE IOH / POH
        // ----------------------------------------------------

        if (
            iohPohText === "" ||
            !/^-?\d+(\.\d+)?$/.test(
                iohPohText
            )
        ) {

            setMessage(
                "Please enter a valid IOH & POH amount"
            );

            setMessageType("error");

            iohPohRef.current?.focus();

            return;

        }


        // ----------------------------------------------------
        // VALIDATE ELECTRICAL
        // ----------------------------------------------------

        if (
            electricalText === "" ||
            !/^-?\d+(\.\d+)?$/.test(
                electricalText
            )
        ) {

            setMessage(
                "Please enter a valid Electrical Manufacturing amount"
            );

            setMessageType("error");

            electricalRef.current?.focus();

            return;

        }


        // ----------------------------------------------------
        // CONVERT
        // ----------------------------------------------------

        const iohPoh =
            Number(iohPohText);

        const electrical =
            Number(electricalText);


        // ----------------------------------------------------
        // EXTRA SAFETY CHECK
        // ----------------------------------------------------

        if (!Number.isFinite(iohPoh)) {

            setMessage(
                "Please enter a valid IOH & POH amount"
            );

            setMessageType("error");

            iohPohRef.current?.focus();

            return;

        }


        if (!Number.isFinite(electrical)) {

            setMessage(
                "Please enter a valid Electrical Manufacturing amount"
            );

            setMessageType("error");

            electricalRef.current?.focus();

            return;

        }


        // ----------------------------------------------------
        // MONTH / YEAR
        // ----------------------------------------------------

        if (!month || !year) {

            setMessage(
                "Month and year not available"
            );

            setMessageType("error");

            return;

        }


        // ----------------------------------------------------
        // SAVE
        // ----------------------------------------------------

        try {

            setSaving(true);


            const payload = {

                IOHPOHJV:
                    iohPoh,

                ELECTRICALJV:
                    electrical,

                PMONTH:
                    Number(month),

                PYEAR:
                    Number(year)

            };


            const res =
                await saveCreditJV(
                    payload
                );


            setMessage(
                res.data.message ||
                "Credit JV data saved successfully"
            );

            setMessageType("success");


            // Reload saved data

            await loadCreditJV(
                Number(month),
                Number(year)
            );


            // Return to first field

            setTimeout(() => {

                iohPohRef.current?.focus();

                iohPohRef.current?.select();

            }, 100);


        } catch (err) {

            console.error(
                "Credit JV save error:",
                err
            );


            setMessage(
                err.response?.data?.message ||
                "Unable to save Credit JV data"
            );

            setMessageType("error");


        } finally {

            setSaving(false);

        }

    };


    // ========================================================
    // ENTER KEY - IOH / POH
    // ========================================================

    const handleIohPohKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            electricalRef.current?.focus();

            electricalRef.current?.select();

        }

    };


    // ========================================================
    // ENTER KEY - ELECTRICAL
    // ========================================================

    const handleElectricalKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            saveRef.current?.focus();

        }

    };


    // ========================================================
    // ENTER KEY - SAVE
    // ========================================================

    const handleSaveKeyDown = (e) => {

        if (e.key === "Enter") {

            e.preventDefault();

            if (!saving) {

                handleSave();

            }

        }

    };


    // ========================================================
    // LOADING SCREEN
    // ========================================================

    if (loading) {

        return (

            <Box
                sx={{
                    width: "100%",
                    minHeight: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >

                <CircularProgress />

            </Box>

        );

    }


    // ========================================================
    // MAIN UI
    // ========================================================

    return (

        <Box
            sx={{
                width: "100%",
                boxSizing: "border-box",

                p: {
                    xs: 0.5,
                    sm: 1,
                    md: 1.5
                }
            }}
        >


            {/* =================================================
                HEADER
            ================================================= */}

           <PageHeader
        title=" Credit JV and Electrical Manufacturing Data Entry"
        subtitle={`Data entry status for the month of: ${monthName} - ${year}`}
        icon={AccessTimeIcon}
       // statusLabel={'Records: ' + totalRows}
       //  statusValue="CSV File"
        // statusChip={{
        //   label: loading
        //     ? "Loading..."
        //     : totalRows > 0
        //       ? "Data Found"
        //       : "No Data",
        //   color: loading ? "warning" : totalRows > 0 ? "primary" : "default",
        // }}
        // showRefresh
        // onRefresh={handleRefresh}
        // refreshing={loading}
      />




      


            {/* =================================================
                ENTRY CARD
            ================================================= */}

            <Card
                elevation={2}
                sx={{
                    width: "100%",
                    borderRadius: 2,
                    boxSizing: "border-box"
                }}
            >

                <CardContent
                    sx={{
                        p: {
                            xs: 1.5,
                            sm: 2,
                            md: 2.5
                        },

                        "&:last-child": {
                            pb: {
                                xs: 1.5,
                                sm: 2,
                                md: 2.5
                            }
                        }
                    }}
                >


                    {/* =========================================
                        ENTRY AREA
                    ========================================= */}

                    <Box
                        sx={{
                            width: "100%",

                            display: "flex",

                            flexDirection: {
                                xs: "column",
                                sm: "row"
                            },

                            alignItems: {
                                xs: "stretch",
                                sm: "center"
                            },

                            gap: {
                                xs: 1.5,
                                sm: 2,
                                md: 3
                            },

                            boxSizing: "border-box"
                        }}
                    >


                        {/* =====================================
                            IOH & POH
                        ===================================== */}

                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: {
                                    xs: 1,
                                    sm: 1.5
                                },

                                flex: 1,

                                minWidth: 0
                            }}
                        >

                            <Typography
                                component="label"
                                htmlFor="iohPohJV"
                                sx={{
                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {
                                        xs: "0.9rem",
                                        sm: "0.95rem"
                                    },

                                    whiteSpace: "nowrap"
                                }}
                            >

                                IOH & POH Foreign Railway

                            </Typography>


                            <TextField
                                id="iohPohJV"

                                inputRef={iohPohRef}

                                type="text"

                                inputMode="decimal"

                                value={iohPohJV}

                                onChange={(e) => {

                                    setIohPohJV(
                                        e.target.value
                                    );

                                }}

                                onKeyDown={
                                    handleIohPohKeyDown
                                }

                                autoComplete="off"

                                size="small"

                                fullWidth

                                sx={{
                                    maxWidth: {
                                        xs: "100%",
                                        sm: 190
                                    },

                                    "& input": {
                                        fontSize: {
                                            xs: "16px",
                                            sm: "15px"
                                        }
                                    }
                                }}
                            />

                        </Box>


                        {/* =====================================
                            ELECTRICAL MANUFACTURING
                        ===================================== */}

                        <Box
                            sx={{
                                display: "flex",

                                alignItems: "center",

                                gap: {
                                    xs: 1,
                                    sm: 1.5
                                },

                                flex: 1,

                                minWidth: 0
                            }}
                        >

                            <Typography
                                component="label"
                                htmlFor="electricalJV"
                                sx={{
                                    color: "#e60023",

                                    fontWeight: 700,

                                    fontSize: {
                                        xs: "0.9rem",
                                        sm: "0.95rem"
                                    },

                                    whiteSpace: "nowrap"
                                }}
                            >

                                Electrical Manufacturing

                            </Typography>


                            <TextField
                                id="electricalJV"

                                inputRef={electricalRef}

                                type="text"

                                inputMode="decimal"

                                value={electricalJV}

                                onChange={(e) => {

                                    setElectricalJV(
                                        e.target.value
                                    );

                                }}

                                onKeyDown={
                                    handleElectricalKeyDown
                                }

                                autoComplete="off"

                                size="small"

                                fullWidth

                                sx={{
                                    maxWidth: {
                                        xs: "100%",
                                        sm: 180
                                    },

                                    "& input": {
                                        fontSize: {
                                            xs: "16px",
                                            sm: "15px"
                                        }
                                    }
                                }}
                            />

                        </Box>


                        {/* =====================================
                            SAVE
                        ===================================== */}

                        <Button
                            ref={saveRef}

                            variant="contained"

                            type="button"

                            onClick={handleSave}

                            onKeyDown={
                                handleSaveKeyDown
                            }

                            disabled={saving}

                            sx={{
                                minWidth: {
                                    xs: "100%",
                                    sm: 110
                                },

                                height: 40,

                                flexShrink: 0,

                                bgcolor: "#ffff00",

                                color: "#000000",

                                border:
                                    "1px solid #d6b500",

                                fontWeight: 700,

                                "&:hover": {
                                    bgcolor: "#ffeb00"
                                }
                            }}
                        >

                            {saving ? (

                                <>

                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            mr: 1,
                                            color: "#000"
                                        }}
                                    />

                                    Saving...

                                </>

                            ) : (

                                "Save"

                            )}

                        </Button>

                    </Box>


                    {/* =================================================
                        MESSAGE
                    ================================================= */}

                    {message && (

                        <Alert
                            severity={
                                messageType === "success"
                                    ? "success"
                                    : "error"
                            }

                            onClose={() => {

                                setMessage("");

                                setMessageType("");

                            }}

                            sx={{
                                mt: 2
                            }}
                        >

                            {message}

                        </Alert>

                    )}

                </CardContent>

            </Card>

        </Box>

    );

};


export default CreditJVDataEntry;